import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Navigation } from '../../components/layout/Navigation';
import { Footer } from '../../components/layout/Footer';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { getPage } from '../../service/app/app.service';
import instructorsMockData from '../../data/instructors.mock.json';
import './instructors.css';

type PageStat = Readonly<{
  label: string;
  value: string;
}>;

type PageHighlight = Readonly<{
  title: string;
  description: string;
}>;

type InstructorProfile = Readonly<{
  id: string;
  name: string;
  title: string;
  tagline: string;
  bio: string;
  avatar: string;
  coverImage: string;
  expertise: ReadonlyArray<string>;
  languages: ReadonlyArray<string>;
  officeHours: string;
  featuredProject: string;
  students: string;
  rating: string;
  yearsExperience: number;
  coursesCount: number;
}>;

type InstructorsPageData = Readonly<{
  kicker: string;
  heroTitle: string;
  heroSubtitle: string;
  stats: ReadonlyArray<PageStat>;
  highlights: ReadonlyArray<PageHighlight>;
  instructors: ReadonlyArray<InstructorProfile>;
}>;

const INSTRUCTORS_MOCK_DATA = instructorsMockData as InstructorsPageData;

const vertexShaderSource = `
  attribute vec2 aPosition;
  varying vec2 vUv;
  void main() {
    vUv = (aPosition + 1.0) * 0.5;
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`;

const fragmentShaderSource = `
  precision mediump float;
  uniform float uTime;
  uniform vec2 uResolution;
  varying vec2 vUv;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }

  void main() {
    vec2 uv = vUv;
    float time = uTime * 0.18;

    vec2 p = uv * vec2(uResolution.x / max(uResolution.y, 1.0), 1.0) * 2.5;
    float n1 = noise(p + vec2(time, -time * 0.7));
    float n2 = noise(p * 1.9 + vec2(-time * 0.6, time));
    float wave = sin((uv.y * 5.0) + (n1 * 2.2) + (time * 4.0));
    float band = smoothstep(0.22, 0.82, n2 + wave * 0.12);

    vec3 deep = vec3(0.02, 0.05, 0.12);
    vec3 gold = vec3(0.94, 0.76, 0.36);
    vec3 mint = vec3(0.28, 1.0, 0.72);
    vec3 blue = vec3(0.16, 0.44, 0.96);

    vec3 color = mix(deep, blue, smoothstep(0.0, 0.9, uv.y));
    color += gold * smoothstep(0.58, 0.95, band) * 0.42;
    color += mint * smoothstep(0.25, 0.8, 1.0 - band) * 0.32;
    color += vec3(noise(uv * 8.5 + time) * 0.03);

    gl_FragColor = vec4(color, 0.85);
  }
`;

const createShader = (
  gl: WebGLRenderingContext,
  shaderType: number,
  source: string
): WebGLShader | null => {
  const shader = gl.createShader(shaderType);
  if (!shader) {
    return null;
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

const createProgram = (
  gl: WebGLRenderingContext,
  vertexShader: WebGLShader,
  fragmentShader: WebGLShader
): WebGLProgram | null => {
  const program = gl.createProgram();
  if (!program) {
    return null;
  }
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }
  return program;
};

const AuroraCanvas: React.FC = () => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
    });

    if (!gl) {
      return undefined;
    }

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vertexShader || !fragmentShader) {
      return undefined;
    }

    const program = createProgram(gl, vertexShader, fragmentShader);
    if (!program) {
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      return undefined;
    }

    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    const buffer = gl.createBuffer();
    if (!buffer) {
      gl.deleteProgram(program);
      return undefined;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1,
      1, -1,
      -1, 1,
      1, 1,
    ]), gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, 'aPosition');
    const timeLocation = gl.getUniformLocation(program, 'uTime');
    const resolutionLocation = gl.getUniformLocation(program, 'uResolution');
    const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.floor(canvas.clientWidth * dpr);
      const height = Math.floor(canvas.clientHeight * dpr);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    };

    let frame = 0;
    const start = performance.now();

    const render = (now: number) => {
      resize();
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
      if (timeLocation) {
        gl.uniform1f(timeLocation, (now - start) / 1000);
      }
      if (resolutionLocation) {
        gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      }
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };

    const loop = (now: number) => {
      render(now);
      frame = window.requestAnimationFrame(loop);
    };

    resize();
    if (reduceMotionQuery.matches) {
      render(start);
    } else {
      frame = window.requestAnimationFrame(loop);
    }

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      window.cancelAnimationFrame(frame);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
  }, []);

  return <canvas ref={canvasRef} className="h-full w-full" aria-hidden="true" />;
};

const InstructorCard: React.FC<{ instructor: InstructorProfile; delay: number }> = ({ instructor, delay }) => {
  return (
    <article className="instructor-card instructors-glow stagger-reveal" style={{ animationDelay: `${delay}ms` }}>
      <div className="relative h-52 overflow-hidden">
        <img
          src={instructor.coverImage}
          alt={`${instructor.name} workspace`}
          className="h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#040916] via-[#040916]/45 to-transparent" />
        <div className="absolute right-4 top-4 rounded-full border border-white/20 bg-black/35 px-3 py-1 text-xs font-semibold text-amber-200 backdrop-blur-sm">
          {instructor.rating} / 5
        </div>
      </div>

      <div className="relative px-6 pb-6">
        <img
          src={instructor.avatar}
          alt={instructor.name}
          className="-mt-9 h-[78px] w-[78px] rounded-2xl border-2 border-white/70 object-cover shadow-xl"
          loading="lazy"
        />
        <h3 className="mt-4 text-2xl font-bold text-white">{instructor.name}</h3>
        <p className="text-sm font-semibold uppercase tracking-wide text-amber-200">{instructor.title}</p>
        <p className="mt-2 text-sm text-slate-300">{instructor.tagline}</p>
        <p className="mt-4 text-sm leading-relaxed text-slate-200/95">{instructor.bio}</p>

        <div className="mt-5 flex flex-wrap gap-2">
          {instructor.expertise.map((topic) => (
            <span key={topic} className="rounded-full border border-slate-500/50 bg-slate-800/65 px-3 py-1 text-xs font-medium text-slate-100">
              {topic}
            </span>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2 rounded-xl border border-slate-500/30 bg-slate-900/45 p-3 text-center">
          <div>
            <div className="text-lg font-bold text-white">{instructor.students}</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-300">Students</div>
          </div>
          <div>
            <div className="text-lg font-bold text-white">{instructor.coursesCount}</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-300">Courses</div>
          </div>
          <div>
            <div className="text-lg font-bold text-white">{instructor.yearsExperience}y</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-300">Experience</div>
          </div>
        </div>

        <div className="mt-4 space-y-2 text-sm text-slate-200">
          <p><span className="font-semibold text-amber-200">Office Hours:</span> {instructor.officeHours}</p>
          <p><span className="font-semibold text-amber-200">Languages:</span> {instructor.languages.join(' · ')}</p>
          <p><span className="font-semibold text-amber-200">Focus Project:</span> {instructor.featuredProject}</p>
        </div>

        <div className="mt-6">
          <Link to="/courses">
            <Button variant="gold" size="sm">
              Explore Instructor Courses
            </Button>
          </Link>
        </div>
      </div>
    </article>
  );
};

const normalizeInstructorsData = (raw: unknown): InstructorsPageData => {
  if (!raw || typeof raw !== 'object') return INSTRUCTORS_MOCK_DATA;
  const data = raw as Partial<InstructorsPageData>;

  return {
    ...INSTRUCTORS_MOCK_DATA,
    ...data,
    stats: Array.isArray(data.stats) && data.stats.length ? data.stats : INSTRUCTORS_MOCK_DATA.stats,
    highlights:
      Array.isArray(data.highlights) && data.highlights.length
        ? data.highlights
        : INSTRUCTORS_MOCK_DATA.highlights,
    instructors:
      Array.isArray(data.instructors) && data.instructors.length
        ? data.instructors
        : INSTRUCTORS_MOCK_DATA.instructors,
  };
};

export const InstructorsPage: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['page', 'instructors'],
    queryFn: async () => {
      const result = await getPage<Partial<InstructorsPageData>>('instructors');
      if (result.error || !result.data) {
        return INSTRUCTORS_MOCK_DATA;
      }

      return normalizeInstructorsData(result.data);
    },
    staleTime: 5 * 60 * 1000,
  });

  const pageData = data || INSTRUCTORS_MOCK_DATA;

  return (
    <div className="instructors-page min-h-screen text-white">
      <Navigation />

      <main className="relative overflow-hidden pb-20 pt-28 md:pt-32">
        {isLoading ? (
          <div className="flex justify-center py-24">
            <Spinner size="xl" />
          </div>
        ) : (
          <>
            <section className="relative">
              <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[560px] opacity-90">
                <AuroraCanvas />
              </div>

              <Container>
                <div className="mx-auto max-w-5xl rounded-[2rem] border border-slate-300/20 bg-slate-950/40 p-8 backdrop-blur-sm md:p-12">
                  <p className="mb-5 inline-flex rounded-full border border-amber-200/60 bg-amber-200/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-100">
                    {pageData.kicker}
                  </p>
                  <h1 className="instructors-title text-5xl font-bold text-white md:text-7xl">
                    {pageData.heroTitle}
                  </h1>
                  <p className="mt-6 max-w-3xl text-lg text-slate-100/90 md:text-xl">
                    {pageData.heroSubtitle}
                  </p>
                  <div className="mt-8 flex flex-wrap gap-4">
                    <Link to="/courses">
                      <Button variant="gold" size="lg">
                        Start Learning
                      </Button>
                    </Link>
                    <Link to="/about">
                      <Button variant="outline" size="lg">
                        Meet Our Mission
                      </Button>
                    </Link>
                  </div>
                </div>

                <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
                  {pageData.stats.map((stat, index) => (
                    <div
                      key={stat.label}
                      className="instructors-glow stagger-reveal rounded-2xl border border-slate-400/25 bg-slate-900/45 p-5 text-center backdrop-blur-sm"
                      style={{ animationDelay: `${index * 90}ms` }}
                    >
                      <p className="text-3xl font-bold text-white">{stat.value}</p>
                      <p className="mt-2 text-sm text-slate-200">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </Container>
            </section>

            <section className="mt-16 md:mt-20">
              <Container>
                <div className="mb-8 flex items-end justify-between gap-5">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-amber-200">How We Teach</p>
                    <h2 className="instructors-title mt-3 text-4xl font-bold text-white md:text-5xl">
                      Mentorship engineered for real outcomes.
                    </h2>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                  {pageData.highlights.map((highlight, index) => (
                    <div
                      key={highlight.title}
                      className="instructors-glow stagger-reveal rounded-2xl border border-slate-400/25 bg-slate-900/45 p-6"
                      style={{ animationDelay: `${index * 110}ms` }}
                    >
                      <p className="text-sm font-semibold uppercase tracking-wide text-amber-200">
                        0{index + 1}
                      </p>
                      <h3 className="mt-3 text-2xl font-bold text-white">{highlight.title}</h3>
                      <p className="mt-3 text-slate-200">{highlight.description}</p>
                    </div>
                  ))}
                </div>
              </Container>
            </section>

            <section className="mt-16 md:mt-20">
              <Container>
                <div className="mb-8 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-amber-200">Meet The Team</p>
                    <h2 className="instructors-title mt-3 text-4xl font-bold md:text-5xl">
                      Instructors Who Ship Product Every Week
                    </h2>
                  </div>
                  <p className="max-w-xl text-slate-200">
                    These are active engineers with teaching systems built around project milestones, pull request quality, and technical communication.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-7 md:grid-cols-2 xl:grid-cols-3">
                  {pageData.instructors.map((instructor, index) => (
                    <InstructorCard
                      key={instructor.id}
                      instructor={instructor}
                      delay={index * 95}
                    />
                  ))}
                </div>
              </Container>
            </section>

            <section className="mt-16 md:mt-20">
              <Container>
                <div className="instructors-glow rounded-2xl border border-amber-300/45 bg-gradient-to-r from-amber-200/16 via-amber-100/10 to-cyan-200/12 p-7 md:p-9">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-200">
                    Join The Instructor Team
                  </p>
                  <h3 className="instructors-title mt-3 text-3xl font-bold text-white md:text-4xl">
                    Interested in becoming an instructor at Master Polyglots?
                  </h3>
                  <p className="mt-4 max-w-4xl text-slate-100/90">
                    We are always open to connecting with experienced engineers, mentors, and
                    builders who care about helping students grow through practical, real-world
                    software training. If this sounds like you, please email
                    {' '}
                    <a
                      href="mailto:clintonjohnson@cmjsoftware.com"
                      className="font-semibold text-amber-200 underline decoration-amber-300/70 underline-offset-4"
                    >
                      clintonjohnson@cmjsoftware.com
                    </a>
                    {' '}
                    with your background, areas of expertise, and any teaching or mentorship experience.
                  </p>
                </div>
              </Container>
            </section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};
